import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faCheck,
    faTimes,
    faShoppingCart,
    faFileAlt,
    faPrint,
    faClipboardList,
    faHistory,
    faEnvelope,
    faComment,
} from '@fortawesome/free-solid-svg-icons';
import { useRole } from '../../context/roleContext';
import { USER_ROLES, AUDIT_LOG_VIEWER_ROLES } from '../../utils/constants';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { formatCurrency } from '../../utils/format';
import AuditLogsModal from '../common/AuditLogsModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { useChannelSettings } from '../../context/ChannelSettingsContext';
import { useMessageAlert } from '../common/MessageAlert';
import { useTranslation } from 'react-i18next';
import {
    canEditChallanRecord,
    canDeleteChallanRecord,
    editDeniedReason,
    deleteDeniedReason,
} from '../../utils/challanRbac';

const formatDate = (dateString, emDash) => {
    if (!dateString) return emDash;
    return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const getStatusBadge = (status, t) => {
    const s = (status || '').toString().toLowerCase();
    const getLabel = (key, fallback) => {
        if (!key) return fallback || String(status);
        return t(`purchaseOrdersModule.purchaseOrders.statuses.${key}`) || fallback || String(status);
    };

    if (s === 'received' || s === 'completed') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }

    if (s === 'confirmed' || s === 'advice_created' || s === 'partial_advice_created' || s === 'supervisor_assigned' || s === 'loading' || s === 'loaded' || s === 'in_transit' || s === 'unloading' || s === 'challan_generated') {
        return (
            <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
                <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, String(status))}</span>
            </div>
        );
    }

    return (
        <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faShoppingCart} className="text-sm" style={{ color: UI_COLORS.STATUS.WARNING }} />
            <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.WARNING }}>{getLabel(s, String(status))}</span>
        </div>
    );
};

const getOrderId = (order) => order?.id || order?.po_id;

// A purchase order's own status never becomes "challan_generated" — the challan lives
// on its advices and the API surfaces it as a flag — so accept either signal.
const hasChallan = (order) =>
    !!order?.challan_generated || (order?.status || '').toLowerCase() === 'challan_generated';

// Statuses past the point where editing a purchase order makes sense at all.
const NON_EDITABLE_STATUSES = ['received', 'completed', 'cancelled', 'rejected'];

// Statuses that mean the goods are already in and the warehouse has been credited.
const RECEIVED_STATUSES = ['received', 'completed'];

// The API rejects every cancel with "Purchase order cancellation is temporarily
// disabled", so the button could only ever surface an error. Hidden until that
// server-side guard is lifted — flip this back on at the same time.
const PO_CANCELLATION_ENABLED = false;

const PurchaseOrdersTable = ({
    orders = [],
    loading = false,
    onEditOrder,
    onDeleteOrder,
    onViewOrder,
    onConfirmOrder,
    onCancelOrder,
    onGenerateAdvice,
    onAssignSupervisor,
    onStartLoading,
    onCompleteLoading,
    onMarkAllAdvised,
    onPrintChallan,
    onGoToAdvice,
    selectedOrders = [],
    onSelectOrder,
    onSelectAll,
    viewLoading = false,
    onSendNotification,
}) => {
    const { t } = useTranslation();
    const { checkDeleteAccess, checkEditAccess, userRole } = useRole();
    const { emailEnabled, whatsappEnabled } = useChannelSettings();
    const { showSuccess, showError } = useMessageAlert();
    const [auditLogTarget, setAuditLogTarget] = useState(null);
    const [showSendDialog, setShowSendDialog] = useState(false);
    const [sendTargetId, setSendTargetId] = useState(null);
    const [pendingChannel, setPendingChannel] = useState('email');
    const [sendLoading, setSendLoading] = useState(false);

    const openSendDialog = (id, channel) => {
        setPendingChannel(channel);
        setSendTargetId(id);
        setShowSendDialog(true);
    };

    // Challan state for this PO. `first_challan_date` is the EARLIEST challan on the
    // order, so "it is today" means every challan on it is — see the API property.
    const challanStateOf = (order) => ({
        userRole,
        challanGenerated: hasChallan(order),
        challanDate: order?.first_challan_date,
    });

    const canEditOrder = useCallback((order) => {
        if (userRole === USER_ROLES.DELIVERY_SUPERVISOR) return false;
        if (NON_EDITABLE_STATUSES.includes((order?.status || '').toLowerCase())) return false;
        // Shared rule: before a challan anyone with write access may edit; after one,
        // Super Admin only and only on the challan's own day.
        if (!canEditChallanRecord(challanStateOf(order))) return false;
        return checkEditAccess(order?.date, { isPurchaseOrder: true });
    }, [userRole, checkEditAccess]);

    const canDeleteOrder = useCallback((order) => {
        if (userRole === USER_ROLES.DELIVERY_SUPERVISOR || !checkDeleteAccess()) return false;
        const status = (order?.status || '').toLowerCase();
        // Goods already in. Super Admin may still delete one — the confirmation asks
        // whether the received stock should be given back — but nobody else may unwind
        // a receipt.
        if (RECEIVED_STATUSES.includes(status) && userRole !== USER_ROLES.SUPER_ADMIN) return false;
        // Shared rule: pre-challan anyone with delete access; post-challan Super Admin
        // only, with no date restriction.
        return canDeleteChallanRecord(challanStateOf(order));
    }, [userRole, checkDeleteAccess]);

    // Where the challan rule is what blocks the action, keep the button visible but
    // disabled with the reason — a silently missing button reads as a broken screen.
    const editBlockedReason = useCallback((order) => {
        if (userRole === USER_ROLES.DELIVERY_SUPERVISOR) return null;
        if (NON_EDITABLE_STATUSES.includes((order?.status || '').toLowerCase())) return null;
        return editDeniedReason(challanStateOf(order)) || null;
    }, [userRole]);

    const deleteBlockedReason = useCallback((order) => {
        if (userRole === USER_ROLES.DELIVERY_SUPERVISOR || !checkDeleteAccess()) return null;
        if (RECEIVED_STATUSES.includes((order?.status || '').toLowerCase())) {
            // Super Admin is no longer blocked here, so the reason belongs to the roles
            // that still are.
            return userRole === USER_ROLES.SUPER_ADMIN
                ? null
                : t('purchaseOrdersModule.purchaseOrders.table.tooltips.deleteReceivedSuperAdminOnly');
        }
        return deleteDeniedReason(challanStateOf(order)) || null;
    }, [userRole, checkDeleteAccess, t]);

    const columns = useMemo(() => [
        {
            id: 'selection',
            sortable: false,
            width: '40px',
            renderHeader: () => {
                const currentPageIds = orders.map(o => o.id || o.po_id);
                const isAllSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedOrders.includes(id));
                return (
                    <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        checked={isAllSelected}
                        onChange={(e) => onSelectAll?.(e.target.checked)} 
                    />
                );
            },
            renderCell: (o) => (
                <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    checked={selectedOrders.includes(o.id || o.po_id)}
                    onChange={(e) => onSelectOrder?.(o.id || o.po_id, e.target.checked)} 
                />
            ),
            skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'po_number',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.poNumber'),
            sortable: true,
            sortAccessor: (o) => (o?.po_number || '').toLowerCase(),
            renderCell: (o) => (
                <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{o.po_number ? o.po_number : t('purchaseOrdersModule.common.symbols.emDash')}</div>
            ),
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'date',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.date'),
            sortable: true,
            sortAccessor: (o) => (o?.date ? new Date(o.date).getTime() : 0),
            renderCell: (o) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{o.date ? formatDate(o.date, t('purchaseOrdersModule.common.symbols.emDash')) : t('purchaseOrdersModule.common.symbols.emDash')}</span>,
            skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'supplier_name',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.supplier'),
            sortable: true,
            sortAccessor: (o) => (o?.supplier_name || '').toLowerCase(),
            renderCell: (o) => <div className="text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{o.supplier_name ? o.supplier_name : t('purchaseOrdersModule.common.symbols.emDash')}</div>,
            skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'delivery_location_name',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.deliveryLocation'),
            sortable: true,
            sortAccessor: (o) => (o?.delivery_location_name || '').toLowerCase(),
            renderCell: (o) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{o.delivery_location_name ? o.delivery_location_name : t('purchaseOrdersModule.common.symbols.emDash')}</span>,
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'total_amount',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.totalAmount'),
            sortable: true,
            sortAccessor: (o) => (o?.total_amount ? parseFloat(o.total_amount) : 0),
            renderCell: (o) => <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{formatCurrency(o.total_amount)}</span>,
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'remark',
            label: t('commonCols.remarks'),
            sortable: true,
            sortAccessor: (o) => (o?.remark || '').toLowerCase(),
            renderCell: (o) => {
                if (!o.remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('purchaseOrdersModule.common.symbols.emDash')}</span>;
                return (
                    <div 
                        className="text-[11px] font-medium break-words leading-relaxed min-w-[160px] max-w-[180px] px-2 py-1.5 rounded border" 
                        title={o.remark} 
                        style={{ 
                            backgroundColor: 'rgba(254, 243, 199, 0.5)', 
                            color: '#92400e', 
                            borderColor: 'rgba(217, 119, 6, 0.2)',
                            whiteSpace: 'pre-wrap',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {o.remark}
                    </div>
                );
            },
            skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'status',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.status'),
            sortable: true,
            align: 'center',
            sortAccessor: (o) => (o?.status || '').toLowerCase(),
            renderCell: (o) => getStatusBadge(o.status, t),
            skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
        },
        {
            id: 'status_actions',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.statusActions'),
            align: 'center',
            sortable: false,
            renderCell: (o) => {
                const status = (o.status || '').toLowerCase();
                const allowedStatuses = ['pending', 'confirmed', 'advice_created', 'partial_advice_created', 'loaded', 'in_progress', 'completed', 'cancelled'];
                const isSupervisor = userRole === USER_ROLES.DELIVERY_SUPERVISOR;
                
                if (status === 'cancelled') {
                    return (
                        <div className="flex items-center justify-center">
                            <span className="text-sm text-gray-600">{t('purchaseOrdersModule.purchaseOrders.table.messages.purchaseCancelled')}</span>
                        </div>
                    );
                }
                if (!allowedStatuses.includes(status) || isSupervisor) return null;

                return (
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {status === 'pending' && (
                            <button
                                onClick={() => onConfirmOrder?.(getOrderId(o))}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.08) }}
                                title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.confirm')}
                            >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                <span className="text-xs font-medium">{t('purchaseOrdersModule.purchaseOrders.table.tooltips.confirm')}</span>
                            </button>
                        )}
                        {status === 'partial_advice_created' && (
                            <button
                                onClick={() => onMarkAllAdvised?.(getOrderId(o))}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.08) }}
                                title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.markAllAdvised')}
                            >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                <span className="text-xs font-medium">{t('purchaseOrdersModule.purchaseOrders.table.tooltips.markAllAdvised')}</span>
                            </button>
                        )}
                        {['confirmed', 'partial_advice_created'].includes(status) && (
                            <button
                                onClick={() => onGenerateAdvice?.(getOrderId(o))}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.08) }}
                                title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.generateAdvice')}
                            >
                                <FontAwesomeIcon icon={faFileAlt} className="text-xs" />
                                <span className="text-xs font-medium">{t('purchaseOrdersModule.purchaseOrders.table.tooltips.generateAdvice')}</span>
                            </button>
                        )}
                        {['advice_created', 'supervisor_assigned', 'loading', 'loaded', 'in_progress', 'in_transit', 'received', 'partial_advice_created', 'unloading'].includes(status) && (
                            <button
                                onClick={() => onGoToAdvice?.(o)}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.08) }}
                                title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.goToAdvice')}
                            >
                                <FontAwesomeIcon icon={faClipboardList} className="text-xs" />
                                <span className="text-xs font-medium">{t('purchaseOrdersModule.purchaseOrders.table.tooltips.goToAdvice')}</span>
                            </button>
                        )}
                        {status === 'received' && (
                            <button
                                onClick={() => onMarkAllAdvised?.(getOrderId(o))}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.08) }}
                                title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.complete')}
                            >
                                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                                <span className="text-xs font-medium">{t('purchaseOrdersModule.purchaseOrders.table.tooltips.complete')}</span>
                            </button>
                        )}
                        {status === 'completed' && (
                            <button
                                onClick={() => onPrintChallan?.(getOrderId(o))}
                                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
                                title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.printChallan')}
                            >
                                <FontAwesomeIcon icon={faPrint} className="text-xs" />
                                <span className="text-xs font-medium">{t('purchaseOrdersModule.purchaseOrders.table.tooltips.printChallan')}</span>
                            </button>
                        )}
                    </div>
                );
            },
            skeleton: (
                <div className="flex items-center justify-center space-x-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="w-8 h-8 bg-gray-300 rounded-lg" />
                    ))}
                </div>
            ),
        },
        {
            id: 'actions',
            label: t('purchaseOrdersModule.purchaseOrders.table.columns.actions'),
            align: 'center',
            sortable: false,
            renderCell: (o) => (
                <div className="flex items-center justify-center space-x-2">
                    <button
                        onClick={() => onViewOrder?.(getOrderId(o))}
                        disabled={viewLoading}
                        className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50"
                        style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
                        title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.view')}
                    >
                        <FontAwesomeIcon icon={faEye} className="text-sm" />
                    </button>

                    {AUDIT_LOG_VIEWER_ROLES.includes(userRole) && (
                        <button
                            onClick={() => setAuditLogTarget({ id: getOrderId(o), label: o.po_number })}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
                            title={t('common.actions.auditLogs')}
                        >
                            <FontAwesomeIcon icon={faHistory} className="text-sm" />
                        </button>
                    )}

                    {onSendNotification && !['cancelled', 'rejected'].includes((o.status || '').toLowerCase()) && userRole !== USER_ROLES.DELIVERY_SUPERVISOR && emailEnabled && (
                        <button
                            onClick={() => openSendDialog(getOrderId(o), 'email')}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
                            title={t('common.actions.sendPoEmail')}
                        >
                            <FontAwesomeIcon icon={faEnvelope} className="text-sm" />
                        </button>
                    )}
                    {onSendNotification && !['cancelled', 'rejected'].includes((o.status || '').toLowerCase()) && userRole !== USER_ROLES.DELIVERY_SUPERVISOR && whatsappEnabled && (
                        <button
                            onClick={() => openSendDialog(getOrderId(o), 'whatsapp')}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: '#ffffff', backgroundColor: '#25D366' }}
                            title={t('common.actions.sendPoWhatsApp')}
                        >
                            <FontAwesomeIcon icon={faComment} className="text-sm" />
                        </button>
                    )}
                    {!canEditOrder(o) && editBlockedReason(o) && (
                        <button
                            disabled
                            className="p-2 rounded-lg opacity-40 cursor-not-allowed"
                            style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) }}
                            title={editBlockedReason(o)}
                        >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                        </button>
                    )}
                    {canEditOrder(o) && (
                        <button
                            onClick={() => onEditOrder?.(getOrderId(o))}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) }}
                            title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.edit')}
                        >
                            <FontAwesomeIcon icon={faEdit} className="text-sm" />
                        </button>
                    )}
                    {PO_CANCELLATION_ENABLED && !['cancelled', 'rejected', 'completed'].includes((o.status || '').toLowerCase()) && userRole !== USER_ROLES.DELIVERY_SUPERVISOR && (
                        <button
                            onClick={() => onCancelOrder?.(getOrderId(o))}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                            title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.cancel')}
                        >
                            <FontAwesomeIcon icon={faTimes} className="text-sm" />
                        </button>
                    )}
                    {!canDeleteOrder(o) && deleteBlockedReason(o) && (
                        <button
                            disabled
                            className="p-2 rounded-lg opacity-40 cursor-not-allowed"
                            style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                            title={deleteBlockedReason(o)}
                        >
                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                        </button>
                    )}
                    {canDeleteOrder(o) && (
                        <button
                            onClick={() => onDeleteOrder?.(getOrderId(o))}
                            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                            style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                            title={t('purchaseOrdersModule.purchaseOrders.table.tooltips.delete')}
                        >
                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                        </button>
                    )}
                </div>
            ),
            skeleton: (
                <div className="flex items-center justify-center space-x-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="w-8 h-8 bg-gray-300 rounded-lg" />
                    ))}
                </div>
            ),
        },
    ], [t, onEditOrder, onDeleteOrder, onViewOrder, onCancelOrder, viewLoading, onConfirmOrder, onMarkAllAdvised, onGoToAdvice, onPrintChallan, onGenerateAdvice, canDeleteOrder, canEditOrder, deleteBlockedReason, editBlockedReason, userRole, setAuditLogTarget, orders, selectedOrders, onSelectOrder, onSelectAll, onSendNotification, emailEnabled, whatsappEnabled]);

    return (
        <>
            <DataTable
                data={orders}
                columns={columns}
                loading={loading}
                enablePagination={false}
                defaultSort={{ field: 'updated_at', direction: 'desc' }}
                keyField="id"
                emptyTitle={t('purchaseOrdersModule.common.table.empty.title')}
                emptyMessage={t('purchaseOrdersModule.common.table.empty.message')}
            />

            <AuditLogsModal
                isOpen={!!auditLogTarget}
                onClose={() => setAuditLogTarget(null)}
                entityType="purchase_order"
                entityId={auditLogTarget?.id}
                entityLabel={auditLogTarget?.label}
                hasTimeline={true}
            />

            <ConfirmDialog
                isOpen={showSendDialog}
                title={`Send Purchase Order via ${pendingChannel === 'whatsapp' ? 'WhatsApp' : 'Email'}`}
                message={`Send this purchase order to the supplier via ${pendingChannel === 'whatsapp' ? 'WhatsApp' : 'email'}?`}
                confirmLabel="Send"
                loadingLabel="Sending..."
                confirmVariant="primary"
                loading={sendLoading}
                onCancel={() => { setShowSendDialog(false); setSendTargetId(null); }}
                onConfirm={async () => {
                    if (!sendTargetId || !onSendNotification) return;
                    setSendLoading(true);
                    try {
                        const result = await onSendNotification(sendTargetId, pendingChannel);
                        if (result && result.success) {
                            showSuccess(pendingChannel === 'whatsapp' ? 'WhatsApp sent successfully' : 'Email sent successfully');
                        } else {
                            showError(result?.error || t('common.messages.failedToSendNotification'));
                        }
                    } catch {
                        showError(t('common.messages.failedToSendNotification'));
                    } finally {
                        setSendLoading(false);
                        setShowSendDialog(false);
                        setSendTargetId(null);
                    }
                }}
            />
        </>
    );
};

export default PurchaseOrdersTable;
